import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

export type User = {
  id: string;
  github_login?: string;
  github_name?: string | null;
  github_avatar_url?: string | null;
  google_email?: string;
  google_name?: string | null;
  google_avatar_url?: string | null;
};

type AuthContextType = {
  user: User | null;
  loading: boolean;
  login: () => void;
  loginGoogle: () => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}

// Use REACT_APP_API_URL when set (Azure/cloud), fall back to same origin (nginx proxy)
const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");

// Dev-only: set REACT_APP_DEV_BYPASS=true in .env to skip auth
const DEV_BYPASS = process.env.REACT_APP_DEV_BYPASS === "true";
const MOCK_USER: User = {
  id: "dev-user",
  github_login: "dev-preview",
  github_name: "Dev Preview",
  github_avatar_url: null,
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(DEV_BYPASS ? MOCK_USER : null);
  const [loading, setLoading] = useState(DEV_BYPASS ? false : true);

  const refreshUser = useCallback(async () => {
    if (DEV_BYPASS) { setUser(MOCK_USER); setLoading(false); return; }
    try {
      const res = await fetch(`${API_BASE}/auth/me`, {
        credentials: "include",
      });

      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = () => {
    // Redirect to GitHub OAuth
    window.location.href = `${API_BASE}/auth/github`;
  };

  const loginGoogle = () => {
    // Redirect to Google OAuth
    window.location.href = `${API_BASE}/auth/google`;
  };

  const logout = async () => {
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // Ignore errors
    }
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginGoogle, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}
